import { supabase } from '../../lib/supabase'
import { todayIso } from '../reports/reportDates'

// muster.availability_kind (supabase/migrations/20260928000000_availability.sql).
export type AvailabilityKind = 'available' | 'preferred' | 'unavailable'

export const AVAILABILITY_KIND_LABEL: Record<AvailabilityKind, string> = {
  available: 'Available',
  preferred: 'Preferred',
  unavailable: 'Unavailable',
}

// 0 = Sunday .. 6 = Saturday, the same numbering the database uses.
export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export type AvailabilityRule = {
  id: string
  weekday: number
  // Both absent for a rule that covers the whole day.
  startTime?: string
  endTime?: string
  kind: AvailabilityKind
}

export type NewAvailabilityRule = Omit<AvailabilityRule, 'id'>

export type ExceptionState = 'pending' | 'approved' | 'returned' | 'denied'

export type AvailabilityException = {
  id: string
  startDate: string
  endDate: string
  startTime?: string
  endTime?: string
  kind: AvailabilityKind
  reason?: string
  state: ExceptionState
  decisionComment?: string
}

export type NewAvailabilityException = Omit<AvailabilityException, 'id' | 'state' | 'decisionComment'>

export type WriteResult = { ok: true } | { ok: false; message: string }

// Postgres sends time as HH:MM:SS; the page works in HH:MM.
const hourMinute = (value: string | null) => (value ? value.slice(0, 5) : undefined)

// "Whole day", or the hours of a window. An end that is not after the start
// runs into the next day.
export function describeWindow(startTime?: string, endTime?: string): string {
  if (!startTime || !endTime) return 'Whole day'
  return endTime <= startTime ? `${startTime} to ${endTime} (next day)` : `${startTime} to ${endTime}`
}

export type ShiftChoice = {
  id: string
  name: string
  startTime: string
  endTime: string
  // The shift the agent is placed on today.
  own: boolean
}

type ShiftRow = { id: string; name: string; start_time: string; end_time: string; team_id: string | null }

/**
 * The shifts an agent can pick as a window: those of their own team and the
 * office-wide ones, their own shift first and the rest by start time.
 */
export async function getMyShiftChoices(userId: string): Promise<ShiftChoice[]> {
  const employeeId = await getMyEmployeeId(userId)
  const today = todayIso()
  const placement = employeeId
    ? await supabase
        .from('employee_assignments')
        .select('team_id, shift_template_id')
        .eq('employee_id', employeeId)
        .eq('active', true)
        .lte('effective_from', today)
        .or(`effective_to.is.null,effective_to.gte.${today}`)
        .maybeSingle()
    : null
  const placed = (placement?.data ?? null) as { team_id: string | null; shift_template_id: string | null } | null

  const { data, error } = await supabase.from('shift_templates').select('id, name, start_time, end_time, team_id').eq('active', true)
  if (error || !data) return []

  return (data as ShiftRow[])
    .filter((row) => row.team_id === null || row.team_id === placed?.team_id)
    .map((row) => ({
      id: row.id,
      name: row.name,
      startTime: row.start_time.slice(0, 5),
      endTime: row.end_time.slice(0, 5),
      own: row.id === placed?.shift_template_id,
    }))
    .sort((a, b) => Number(b.own) - Number(a.own) || a.startTime.localeCompare(b.startTime))
}

async function getMyEmployeeId(userId: string): Promise<string | null> {
  const { data } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  return data?.id ?? null
}

type RuleRow = { id: string; weekday: number; start_time: string | null; end_time: string | null; kind: AvailabilityKind }

// The rules in effect today or later, in weekday and time order.
export async function getMyAvailabilityRules(userId: string): Promise<AvailabilityRule[]> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return []
  const { data, error } = await supabase
    .from('availability_rules')
    .select('id, weekday, start_time, end_time, kind')
    .eq('employee_id', employeeId)
    .or(`effective_to.is.null,effective_to.gte.${todayIso()}`)
    .order('weekday', { ascending: true })
    .order('start_time', { ascending: true })
  if (error || !data) return []
  return (data as RuleRow[]).map((row) => ({
    id: row.id,
    weekday: row.weekday,
    startTime: hourMinute(row.start_time),
    endTime: hourMinute(row.end_time),
    kind: row.kind,
  }))
}

const OVERLAP_MESSAGE = 'That overlaps a rule you already have for the same day.'
const FAILED_MESSAGE = 'The change could not be saved. Try again.'

// 23P01 is Postgres's exclusion violation: two rules for one weekday that
// cover the same time.
function writeFailure(error: { code?: string }): WriteResult {
  return { ok: false, message: error.code === '23P01' ? OVERLAP_MESSAGE : FAILED_MESSAGE }
}

export async function addAvailabilityRule(userId: string, rule: NewAvailabilityRule): Promise<WriteResult> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return { ok: false, message: FAILED_MESSAGE }
  const { error } = await supabase.from('availability_rules').insert({
    employee_id: employeeId,
    weekday: rule.weekday,
    start_time: rule.startTime ?? null,
    end_time: rule.endTime ?? null,
    kind: rule.kind,
    effective_from: todayIso(),
    created_by: userId,
    updated_by: userId,
  })
  return error ? writeFailure(error) : { ok: true }
}

function dayBefore(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day - 1))
  return date.toISOString().slice(0, 10)
}

// A rule is effective-dated, so removing one that has taken effect ends it as
// of yesterday and leaves the days it covered as they were. A rule that has
// not taken effect yet (it starts today or later) covered nothing, and is
// deleted.
export async function removeAvailabilityRule(userId: string, id: string): Promise<WriteResult> {
  const { data, error: readError } = await supabase.from('availability_rules').select('effective_from').eq('id', id).maybeSingle()
  if (readError || !data) return { ok: false, message: FAILED_MESSAGE }

  const today = todayIso()
  const { error } =
    (data as { effective_from: string }).effective_from >= today
      ? await supabase.from('availability_rules').delete().eq('id', id)
      : await supabase.from('availability_rules').update({ effective_to: dayBefore(today), updated_by: userId }).eq('id', id)
  return error ? { ok: false, message: FAILED_MESSAGE } : { ok: true }
}

type ExceptionRow = {
  id: string
  start_date: string
  end_date: string
  start_time: string | null
  end_time: string | null
  kind: AvailabilityKind
  reason: string | null
  state: ExceptionState
  decisions: { comment: string | null; decided_at: string }[] | null
}

// Newest first, each with the comment on its latest decision.
export async function getMyAvailabilityExceptions(userId: string): Promise<AvailabilityException[]> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return []
  const { data, error } = await supabase
    .from('availability_exceptions')
    .select('id, start_date, end_date, start_time, end_time, kind, reason, state, decisions(comment, decided_at)')
    .eq('employee_id', employeeId)
    .order('start_date', { ascending: false })
  if (error || !data) return []
  return (data as ExceptionRow[]).map((row) => {
    const decision = (row.decisions ?? []).reduce<{ comment: string | null; decided_at: string } | undefined>(
      (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
      undefined,
    )
    return {
      id: row.id,
      startDate: row.start_date,
      endDate: row.end_date,
      startTime: hourMinute(row.start_time),
      endTime: hourMinute(row.end_time),
      kind: row.kind,
      reason: row.reason ?? undefined,
      state: row.state,
      decisionComment: decision?.comment ?? undefined,
    }
  })
}

function exceptionFields(exception: NewAvailabilityException) {
  return {
    start_date: exception.startDate,
    end_date: exception.endDate,
    start_time: exception.startTime ?? null,
    end_time: exception.endTime ?? null,
    kind: exception.kind,
    reason: exception.reason?.trim() ? exception.reason.trim() : null,
  }
}

// A new exception starts pending, for a supervisor to decide.
export async function submitAvailabilityException(userId: string, exception: NewAvailabilityException): Promise<WriteResult> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return { ok: false, message: FAILED_MESSAGE }
  const { error } = await supabase.from('availability_exceptions').insert({
    employee_id: employeeId,
    ...exceptionFields(exception),
    state: 'pending',
    created_by: userId,
    updated_by: userId,
  })
  return error ? { ok: false, message: FAILED_MESSAGE } : { ok: true }
}

// Edits a returned exception and sends it back to the supervisor.
export async function resubmitAvailabilityException(userId: string, id: string, exception: NewAvailabilityException): Promise<WriteResult> {
  const { error } = await supabase
    .from('availability_exceptions')
    .update({ ...exceptionFields(exception), state: 'pending', decided_by: null, decided_at: null, updated_by: userId })
    .eq('id', id)
  return error ? { ok: false, message: FAILED_MESSAGE } : { ok: true }
}

// Withdraws an exception that has not been decided.
export async function withdrawAvailabilityException(id: string): Promise<WriteResult> {
  const { error } = await supabase.from('availability_exceptions').delete().eq('id', id)
  return error ? { ok: false, message: FAILED_MESSAGE } : { ok: true }
}
